import { formatNumber, registerLocaleData } from '@angular/common';
import { ChangeDetectorRef, Injectable, Injector } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import Utils from '@core/utils';
import { TranslateService } from '@ngx-translate/core';
import { IntlService, CldrIntlService } from '@progress/kendo-angular-intl';
import * as moment from 'moment';
import { Subject, Observable } from 'rxjs';
import { first } from 'rxjs/operators';

import { CustomTranslateLoader } from './custom-translate-loader.service';
import { CURRENCY_FORMAT, CURRENCY_SYMBOL, DATE_FORMAT } from './i18n.constants';
import { DateAdapter } from '@angular/material/core';
import {translateApiUrl} from '../../../api.urls';


@Injectable({
  providedIn: 'root'
})
export class I18nService {

  loading = false;
  loadingCore = false;

  router: Router;
  injector: Injector;
  translateService: TranslateService;
  titleService: Title;
  dateAdapter: DateAdapter<any>;
  chRef: ChangeDetectorRef;

  private localeChangeSubj$ = new Subject<string>();
  localeChange$ = this.localeChangeSubj$.asObservable();

  constructor(public intlService: IntlService) { }

  get locale(): string {
    return this.translateService.currentLang;
  }

  changeDependencies(injector: Injector): void {
    this.injector = injector;
    this.router = injector.get(Router);
    this.titleService = injector.get(Title);
    this.translateService = injector.get(TranslateService);
    this.dateAdapter = injector.get(DateAdapter);
    this.chRef = injector.get(ChangeDetectorRef);
  }

  async load(injector = this.injector): Promise<void> {
    const router = injector.get(Router);
    const titleService = injector.get(Title);
    const translateService = injector.get(TranslateService);
    const dateAdapter = injector.get(DateAdapter);
    const chRef = injector.get(ChangeDetectorRef);
    const locale = 'en';
    const translateLoader = translateService.currentLoader as CustomTranslateLoader;
    const shouldChangeUrl = (translateLoader.translateApiUrl !== translateApiUrl);


    const shouldReloadTranslations = (
      shouldChangeUrl ||
      (!translateService.translations || !translateService.translations[locale])
    );

    this.loading = true;
    this.loadingCore = true;
    if (shouldReloadTranslations) {
      await translateService.getTranslation(locale).pipe(first()).toPromise();
    }
    await translateService.use(locale).toPromise().then().finally(() => {

      if (translateLoader.route !== 'core') {
        this.loading = false;
      } else {
        this.loadingCore = false;
      }
      return;
    });
    await this.registerLocaleData(locale);
    moment.locale(locale);
    dateAdapter.setLocale(locale);

    (this.intlService as CldrIntlService).localeId = locale;

    this.setTitlePage(translateService);

    this.localeChangeSubj$.next(locale);
    chRef.detectChanges();
  }

  public setTitlePage(translateService: TranslateService): void {
    const currentPage = Utils.getModulePath(this.router.url);
    const appTitle = translateService.instant('lbl_app_title.text');
    const pageTitle = translateService.instant(`lbl_page_title_${currentPage}.text`);
    // this.titleService.setTitle(`${appTitle} | ${pageTitle}`);
  }

  translate(key: string | string[], interpolateParams?: any): Observable<any> {
    return this.translateService.get(key, interpolateParams);
  }

  translateInstant(key: string | string[], interpolateParams?: any): any {
    return this.translateService.instant(key, interpolateParams);
  }

  formatNumber(value: number, format = null, locale = this.locale): string {
    return formatNumber(value, locale, format);
  }

  formatDate(value: string | number | Date, format = DATE_FORMAT, locale = this.locale): string {
    return moment(value).format(format);
  }

  formatCurrency(value: number, format = CURRENCY_FORMAT, locale = this.locale): string {
    return formatNumber(value, locale, format) + ' ' + CURRENCY_SYMBOL;
  }

  private registerLocaleData(locale = this.locale): Promise<any> {
    return import(
      /* webpackInclude: /(bg|cs|da|nl|en|et|fi|fr|de|el|hu|ga|it|lv|lt|mt|pl|pt|ro|sk|sl|es|sv)\.js$/ */
      `@angular/common/locales/${locale}.js`
    ).then(module => registerLocaleData(module.default));
  }

}
